import * as React from "react";
import { DataTable } from "components/DataTable/DataTable";
import { ClientMtUsageReportItem, ClientMtUsageReport, CharactersChargedByMtService } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { Stack, Tooltip, Typography } from "@mui/material";
import { describeMtBillingType } from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { formatDate, secondsToMinutes } from "utils/dateUtils";
import { TokenBasedPaginator } from "components/TokenBasedPaginator/TokenBasedPaginator";

interface Props {
  data?: ClientMtUsageReport;
  isLoading: boolean;
  error?: unknown;
  rowsPerPage: number;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onPageTokenChange: (pageToken: string | undefined) => void;
  nextPageToken: string | undefined;
  paginatorResetRef: React.MutableRefObject<() => void>;
}

const MtClientUsageReportTable: React.FC<Props> = ({
  data,
  isLoading,
  error,
  rowsPerPage,
  onRowsPerPageChange,
  onPageTokenChange,
  nextPageToken,
  paginatorResetRef
}) => {
  const charactersChargedCell = React.useCallback((row: ClientMtUsageReportItem) => {
    const { charactersChargedByMtService } = row;
    const tooltipItems: string[] = charactersChargedByMtService
      ? Object.keys(charactersChargedByMtService)
          .filter(serviceId => ["AZURE", "INTENTO"].includes(serviceId))
          .map(
            serviceId =>
              `${serviceId}: ${charactersChargedByMtService[serviceId as keyof CharactersChargedByMtService] || 0}`
          )
      : [];

    const conditionalSx = tooltipItems.length
      ? {
          textDecoration: "underline",
          textDecorationStyle: "dotted", // Apply dotted style
          textDecorationColor: "currentcolor", // Use the current text color
          textDecorationThickness: "2px", // Set the thickness of the underline
          textUnderlineOffset: "3px" // Adjust the offset of the underline
        }
      : {};
    return (
      <Tooltip title={tooltipItems.join(", ")}>
        <Typography variant="body2" component="span" sx={conditionalSx}>
          {(charactersChargedByMtService?.AZURE || 0) + (charactersChargedByMtService?.INTENTO || 0)}
        </Typography>
      </Tooltip>
    );
  }, []);

  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "clientName",
            title: "Client Name",
            cell: (row: ClientMtUsageReportItem) => row.clientName
          },
          {
            id: "clientId",
            title: "Client ID",
            cell: (row: ClientMtUsageReportItem) => row.clientId
          },
          {
            id: "branchName",
            title: "Branch",
            cell: (row: ClientMtUsageReportItem) => row.branchName || ""
          },
          {
            id: "salesRep",
            title: "Sales Rep",
            cell: (row: ClientMtUsageReportItem) => row.salesRep || ""
          },
          {
            id: "appBundleName",
            title: "App Bundle",
            cell: (row: ClientMtUsageReportItem) => row.appBundleName || ""
          },
          {
            id: "clientContacts",
            title: "User Count",
            cell: (row: ClientMtUsageReportItem) => row.usernames.length
          },
          {
            id: "billingType",
            title: "Billing Type",
            cell: (row: ClientMtUsageReportItem) => describeMtBillingType(row.billingType)
          },
          {
            id: "billingTypeLastModificationDate",
            title: "Billing Type Last Modification Date",
            cell: (row: ClientMtUsageReportItem) =>
              row.billingTypeLastModificationDate ? formatDate(row.billingTypeLastModificationDate) : ""
          },
          {
            id: "requestCount",
            title: "Request Count",
            cell: (row: ClientMtUsageReportItem) => row.requestCount
          },
          {
            id: "charactersCharged",
            title: "Characters Charged",
            cell: charactersChargedCell
          },
          {
            id: "textCharactersCharged",
            title: "Text Translation (characters)",
            cell: (row: ClientMtUsageReportItem) => row.charactersChargedByUserRequest?.TEXT_TRANSLATION || 0
          },
          {
            id: "documentCharactersCharged",
            title: "Document Translation (characters)",
            cell: (row: ClientMtUsageReportItem) => row.charactersChargedByUserRequest?.FILES_TRANSLATION || 0
          },
          {
            id: "mediaTranscriptionDuration",
            title: "Transcription (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0)
          },
          {
            id: "mediaTranscriptionAndTranslationDuration",
            title: "Transcription And Translation (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0)
          },
          {
            id: "mediaTransclationAndDubbingDuration",
            title: "Translation And Dubbing (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_DUBBING || 0)
          },
          {
            id: "mediaTranslationAndSubtitlingDuration",
            title: "Translation And Subtitling (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0)
          }
        ]}
        data={data?.items || []}
        rowKey={(row: ClientMtUsageReportItem) => row.clientId}
      />
      <TokenBasedPaginator
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageTokenChange={onPageTokenChange}
        nextPageToken={nextPageToken}
        currentPageDataRows={data?.items.length}
        rowsPerPageOptions={[10, 20, 50, 100]}
        resetRef={paginatorResetRef}
      />
    </Stack>
  );
};

export { MtClientUsageReportTable };
